import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import OwnerSidebar from "./OwnerSidebar";
import OwnerHeader from "./OwnerHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
//import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Building2, 
  Users, 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  CheckCircle2,
  ArrowUp,
  Activity
} from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { db, auth } from "../firebase/config"; // Adjust path if needed to match your project structure
import { collection, getCountFromServer, getDocs, getDoc, doc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import ContentLoader from "../authentication/ContentLoader";

// Mock data for charts (unchanged)
const schoolGrowthData = [
  { month: "Jan", schools: 45 },
  { month: "Feb", schools: 52 },
  { month: "Mar", schools: 61 },
  { month: "Apr", schools: 73 },
  { month: "May", schools: 89 },
  { month: "Jun", schools: 102 },
];

const revenueData = [
  { month: "Jan", revenue: 12500 },
  { month: "Feb", revenue: 15200 },
  { month: "Mar", revenue: 18900 },
  { month: "Apr", revenue: 22100 },
  { month: "May", revenue: 27300 },
  { month: "Jun", revenue: 31800 },
];

const userActivityData = [
  { day: "Mon", active: 1240 },
  { day: "Tue", active: 1385 },
  { day: "Wed", active: 1520 },
  { day: "Thu", active: 1480 },
  { day: "Fri", active: 1650 },
  { day: "Sat", active: 980 },
  { day: "Sun", active: 870 },
];

const recentActivities = [
  { id: 1, type: "new_school", message: "St. Mary's Academy registered", time: "2 minutes ago", icon: Building2, color: "text-green-600" },
  { id: 2, type: "payment", message: "Payment confirmed: Lincoln High School - $299/mo", time: "15 minutes ago", icon: CheckCircle2, color: "text-blue-600" },
  { id: 3, type: "alert", message: "Failed payment: Riverside Elementary", time: "1 hour ago", icon: AlertCircle, color: "text-red-600" },
  { id: 4, type: "new_school", message: "Oakwood School District registered", time: "2 hours ago", icon: Building2, color: "text-green-600" },
  { id: 5, type: "payment", message: "Payment confirmed: Westside Academy - $499/mo", time: "3 hours ago", icon: CheckCircle2, color: "text-blue-600" },
];

// Define interfaces (matching the form)
interface Affiliation {
  role: string;
  schoolID?: string;
}

interface School {
  id: string;
  name: string;
  email?: string;
  address?: string;
  type?: string;
  county?: string;
  createdAt?: string;
  createdBy?: string;
}

const OwnerDashboard = () => {
  const [activeSection, setActiveSection] = useState("Dashboard");
  const [totalSchools, setTotalSchools] = useState(0);
  const [schoolsList, setSchoolsList] = useState<School[]>([]);
  const [showSchoolsList, setShowSchoolsList] = useState(false);
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(true);

  // Load user data and set total schools count from Firebase auth claims (exact pattern from SchoolRegistrationForm, adapted for owner)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          if (process.env.NODE_ENV !== "production") {
            console.log("Authenticating user with uid:", currentUser.uid);
          }
          // Refresh token to ensure claims are up-to-date
          await currentUser.getIdToken(true);
          const idTokenResult = await currentUser.getIdTokenResult(true);
          if (!idTokenResult) {
            throw new Error("Failed to retrieve user token claims");
          }
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          if (process.env.NODE_ENV !== "production") {
            console.log("ID Token Result claims:", idTokenResult.claims);
            console.log("Affiliations:", affiliations, "Default Affiliation:", defaultAffiliation, "Claim schoolID:", claimSchoolID);
          }

          // Select affiliation similar to App.tsx, but check for owner role
          let selectedAffiliation: Affiliation | null = null;
          if (affiliations.length > 0) {
            if (defaultAffiliation && defaultAffiliation.role === "owner") {
              selectedAffiliation = defaultAffiliation;
            } else {
              selectedAffiliation = affiliations.find(aff => aff.role === "owner") || defaultAffiliation || affiliations[0];
            }
          }

          if (selectedAffiliation?.role === "owner") {
            if (process.env.NODE_ENV !== "production") {
              console.log("Using owner affiliation:", selectedAffiliation);
            }

            // Fetch total schools count
            const schoolsCol = collection(db, "schools");
            const snapshot = await getCountFromServer(schoolsCol);
            setTotalSchools(snapshot.data().count);
          } else {
            if (process.env.NODE_ENV !== "production") {
              console.log("No valid selectedAffiliation for owner role, selectedAffiliation:", selectedAffiliation);
            }
            // Optionally toast error, but for dashboard, just log
          }

          setIsLoadingCount(false);
        } catch (err: any) {
          console.error("Error loading user data for uid:", currentUser.uid, err);
          setIsLoadingCount(false);
        }
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.log("No user authenticated");
        }
        setIsLoadingCount(false);
      }
    });

    return () => {
      if (process.env.NODE_ENV !== "production") {
        console.log("Unsubscribing from onAuthStateChanged");
      }
      unsubscribe();
    };
  }, []);

  // Fetch schools list on demand (when clicked) - no auth recheck needed as it's after initial load
  const handleTotalSchoolsClick = async () => {
    console.log("Total Schools card clicked"); // Debug log
    if (isLoadingSchools) return;

    // If list is already loaded, just toggle the dialog
    if (schoolsList.length > 0) {
      setShowSchoolsList(!showSchoolsList);
      return;
    }

    setIsLoadingSchools(true);
    try {
      const schoolsCol = collection(db, "schools");
      const schoolsSnapshot = await getDocs(schoolsCol);
      const schoolsPromises = schoolsSnapshot.docs.map(async (schoolDoc) => {
        const schoolID = schoolDoc.id;
        const detailsDoc = await getDoc(doc(db, "schools", schoolID, "schoolDetails", "main"));
        const schoolData = detailsDoc.exists() ? detailsDoc.data() : {};
        const createdAtDate = schoolData.createdAt ? new Date(schoolData.createdAt).toLocaleDateString() : '';
        return {
          id: schoolID,
          name: schoolData.name || schoolID,
          email: schoolData.email,
          address: schoolData.address,
          type: schoolData.type,
          county: schoolData.county,
          createdAt: createdAtDate,
          createdBy: schoolData.createdBy,
        };
      });
      let schoolsData = await Promise.all(schoolsPromises);
      // Sort by name
      schoolsData = schoolsData.sort((a, b) => a.name.localeCompare(b.name));
      setSchoolsList(schoolsData);
      console.log("Schools loaded:", schoolsData.length); // Debug log
    } catch (err: any) {
      console.error("Error fetching schools list:", err);
    } finally {
      setIsLoadingSchools(false);
      setShowSchoolsList(true);
    }
  };

  const closeSchoolsList = () => {
    setShowSchoolsList(false);
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <OwnerSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          userRole="owner"
        />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <OwnerHeader />
          <div className="flex-1 overflow-y-auto p-3 sm:p-6">
            <ContentLoader isLoading={isLoadingCount} message="">
              <div className="space-y-8">
                {/* Header */}
                <div className="space-y-2">
                  <h1 className="text-3xl font-bold tracking-tight">Platform Overview</h1>
                  <p className="text-muted-foreground">Monitor and manage all schools, users, and platform operations</p>
                </div>

                {/* Top KPI Cards */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <Card 
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={handleTotalSchoolsClick}
                  >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Total Schools</CardTitle>
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {totalSchools}
                      </div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <ArrowUp className="h-3 w-3 text-green-600" />
                        <span className="text-green-600">+14.5%</span> from last month
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">24,587</div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <ArrowUp className="h-3 w-3 text-green-600" />
                        <span className="text-green-600">+8.2%</span> from last month
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">$31,800</div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <ArrowUp className="h-3 w-3 text-green-600" />
                        <span className="text-green-600">+16.4%</span> from last month
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-sm font-medium">Support Tickets</CardTitle>
                      <Activity className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">12 / 45</div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Open / Total this week
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Schools List Popup */}
                <Dialog open={showSchoolsList} onOpenChange={closeSchoolsList}>
                  <DialogContent className="max-w-7xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>All Registered Schools</DialogTitle>
                      <DialogDescription>Full list of schools in the platform</DialogDescription>
                    </DialogHeader>
                    <ContentLoader isLoading={isLoadingSchools} message="">
                    {schoolsList.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>County</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Address</TableHead>
                            <TableHead>Created At</TableHead>
                            <TableHead>Created By</TableHead>
                            <TableHead>ID</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {schoolsList.map((school) => (
                            <TableRow key={school.id}>
                              <TableCell>{school.name}</TableCell>
                              <TableCell>{school.email || 'N/A'}</TableCell>
                              <TableCell>{school.county || 'N/A'}</TableCell>
                              <TableCell>{school.type || 'N/A'}</TableCell>
                              <TableCell className="max-w-xs truncate">{school.address || 'N/A'}</TableCell>
                              <TableCell>{school.createdAt || 'N/A'}</TableCell>
                              <TableCell>{school.createdBy || 'N/A'}</TableCell>
                              <TableCell className="text-xs font-mono text-muted-foreground">{school.id}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p>No schools found.</p>
                    )}
                    </ContentLoader>
                  </DialogContent>
                </Dialog>

                {/* Charts Row */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* School Growth Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>School Growth Trend</CardTitle>
                      <CardDescription>Total registered schools over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={schoolGrowthData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line 
                            type="monotone" 
                            dataKey="schools" 
                            stroke="hsl(var(--primary))" 
                            strokeWidth={2}
                            name="Schools"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Revenue Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Revenue Trend</CardTitle>
                      <CardDescription>Monthly subscription income</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={revenueData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value) => `$${value}`}
                          />
                          <Legend />
                          <Bar 
                            dataKey="revenue" 
                            fill="hsl(var(--primary))"
                            name="Revenue ($)"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* User Activity Heatmap */}
                <Card>
                  <CardHeader>
                    <CardTitle>User Activity</CardTitle>
                    <CardDescription>Active users throughout the week</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={userActivityData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          dataKey="active" 
                          fill="hsl(var(--chart-2))"
                          name="Active Users"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Recent Activity Feed & Quick Actions */}
                <div className="grid gap-4 md:grid-cols-3">
                  {/* Recent Activity */}
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle>Recent Activity</CardTitle>
                      <CardDescription>Latest platform events and notifications</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {recentActivities.map((activity) => (
                          <div key={activity.id} className="flex items-start gap-4 pb-4 border-b last:border-0">
                            <div className={`mt-1 ${activity.color}`}>
                              <activity.icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1 space-y-1">
                              <p className="text-sm font-medium">{activity.message}</p>
                              <p className="text-xs text-muted-foreground">{activity.time}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Quick Actions */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Quick Actions</CardTitle>
                      <CardDescription>Common tasks and shortcuts</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <button className="w-full flex items-center gap-2 p-3 text-sm font-medium rounded-lg border hover:bg-accent transition-colors">
                          <Building2 className="h-4 w-4" />
                          Add New School
                        </button>
                        <button className="w-full flex items-center gap-2 p-3 text-sm font-medium rounded-lg border hover:bg-accent transition-colors">
                          <DollarSign className="h-4 w-4" />
                          Manage Subscriptions
                        </button>
                        <button className="w-full flex items-center gap-2 p-3 text-sm font-medium rounded-lg border hover:bg-accent transition-colors">
                          <Users className="h-4 w-4" />
                          View All Users
                        </button>
                        <button className="w-full flex items-center gap-2 p-3 text-sm font-medium rounded-lg border hover:bg-accent transition-colors">
                          <AlertCircle className="h-4 w-4" />
                          Contact Support
                        </button>
                        <button className="w-full flex items-center gap-2 p-3 text-sm font-medium rounded-lg border hover:bg-accent transition-colors">
                          <TrendingUp className="h-4 w-4" />
                          View Analytics
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              <Outlet />
            </ContentLoader>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default OwnerDashboard;